import {
  Box,
  Typography,
  useTheme,
  Card,
  CardContent,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  AlertTitle,
  LinearProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tokens } from '../../theme';
import React, { useEffect, useState, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import moment from 'moment';
import fetchData from '../../helpers/fetchData';
import ChurnKpiTrendModal from './ChurnKpiTrendModal';

interface GrowthData {
  date: string;
  new_signups: number;
  new_subscribed: number;
  new_unsubscribed: number;
  new_active_subscribed: number;
  new_inactive_subscribed: number;
  cumulative_users: number;
  cumulative_active_subscribed: number;
  cumulative_inactive_subscribed: number;
  cumulative_unsubscribed: number;
}

interface ChurnMetric {
  avg: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  count: number;
}

interface LifetimeStatistics {
  cohort_start_date: string;
  cohort_end_date?: string;
  inactive_rule_days: number;
  cohort_size: number;
  exit_count: number;
  exit_rate: number;
  unsubscribe: ChurnMetric;
  inactive: ChurnMetric;
  exit: ChurnMetric;
  engagement_window: ChurnMetric;
  never_opened_count: number;
}

interface DistributionData {
  lifetime_bucket: string;
  count: number;
}

interface SummaryData {
  total_users: number;
  subscribed_users: number;
  unsubscribed_users: number;
  active_subscribed: number;
  inactive_subscribed: number;
  earliest_signup: string;
  latest_signup: string;
  signups_last_7_days: number;
  signups_last_30_days: number;
}

interface MigrationData {
  date: string;
  migrations_to_mailchimp: number;
  migrations_to_iterable: number;
  cumulative_migrations_to_mailchimp: number;
  cumulative_migrations_to_iterable: number;
}

interface MigrationReasonsData {
  migration_type: string;
  reason: string;
  count: number;
}

interface ListStateData {
  date: string;
  total_users: number;
  active_subscribed: number;
  inactive_subscribed: number;
  unsubscribed_users: number;
}

interface UserStatisticsTabProps {
  startDate: string;
  endDate: string;
  aggregation: string;
}

interface KpiCardProps {
  title: string;
  tooltip: string;
  value: string;
  valueColor: string;
  subtitle?: string;
  colors: any;
  onTrendClick?: () => void;
}

const renderKpiCard = ({
  title,
  tooltip,
  value,
  valueColor,
  subtitle,
  colors,
  onTrendClick,
}: KpiCardProps) => (
  <Card
    sx={{
      backgroundColor: colors.primary[400],
      height: '100%',
      cursor: onTrendClick ? 'pointer' : 'default',
      transition: 'outline-color 120ms ease',
      outline: '2px solid transparent',
      '&:hover': onTrendClick ? { outline: `2px solid ${colors.blueAccent[500]}` } : undefined,
    }}
    onClick={onTrendClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
          {title}
        </Typography>
        <Tooltip title={tooltip} arrow placement="top">
          <InfoOutlinedIcon
            sx={{
              fontSize: 16,
              color: colors.grey[300],
              cursor: 'help',
              ml: 0.25,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
        {onTrendClick && (
          <Tooltip title="Verlauf über Zeit (Kohorten-Monate) anzeigen" arrow placement="top">
            <Box
              component="span"
              sx={{
                ml: 'auto',
                fontSize: 14,
                color: colors.blueAccent[500],
                opacity: 0.8,
              }}
            >
              📈
            </Box>
          </Tooltip>
        )}
      </Box>
      <Typography color={valueColor} variant="h3" fontWeight="bold" mt={1}>
        {value}
      </Typography>
      {subtitle && (
        <Typography color={colors.grey[300]} variant="body2">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const renderCohortGroupLegend = (
  stats: LifetimeStatistics,
  colors: any,
  formatNumber: (n: number) => string,
) => {
  const stillActive = stats.cohort_size - stats.exit_count;
  const pct = (n: number) => ((n / stats.cohort_size) * 100).toFixed(1) + ' %';
  const overlap = Math.max(stats.unsubscribe.count + stats.inactive.count - stats.exit_count, 0);

  const groups = [
    {
      label: 'Noch aktiv',
      count: stillActive,
      color: colors.greenAccent[500],
      desc: 'Kein Exit-Ereignis bisher',
    },
    {
      label: 'Abgemeldet',
      count: stats.unsubscribe.count,
      color: colors.redAccent[500],
      desc: "'Abmelden'-Link geklickt",
    },
    {
      label: 'Stille Abwanderer (mit Opens)',
      count: stats.engagement_window.count,
      color: colors.blueAccent[500],
      desc: `${stats.inactive_rule_days}+ Tage keine Mail mehr geöffnet, aber vorher mindestens 1 Open`,
    },
    {
      label: 'Nie geöffnet',
      count: stats.never_opened_count,
      color: colors.grey[400],
      desc: `${stats.inactive_rule_days}+ Tage keine Mail geöffnet und auch vorher kein einziger Open`,
    },
  ];

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: colors.primary[400],
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h6" fontWeight="600" color={colors.grey[100]}>
          Zustände in der Kohorte
        </Typography>
        <Tooltip
          title="Diese Gruppen können sich überschneiden — ein Nutzer kann gleichzeitig abgemeldet UND inaktiv sein (Beispiel: meldet sich ab nach 45 Tagen, ist aber zu dem Zeitpunkt schon seit 90 Tagen ohne Open). Solche Nutzer werden in beiden Gruppen gezählt. Deshalb summieren die Prozente nicht zu 100 %. Die Überlappungszahl steht direkt unter den Boxen."
          arrow
          placement="top"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: colors.grey[300], cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Grid container spacing={1.5}>
        {groups.map((g) => (
          <Grid item xs={12} sm={6} md={3} key={g.label}>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'rgba(255,255,255,0.04)',
                borderLeft: `4px solid ${g.color}`,
                borderRadius: 0.5,
                height: '100%',
              }}
            >
              <Typography variant="body2" color={colors.grey[100]} sx={{ lineHeight: 1.3 }}>
                <strong style={{ color: g.color }}>{g.label}</strong>
              </Typography>
              <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mt={0.5}>
                {formatNumber(g.count)}{' '}
                <Typography component="span" variant="body2" color={colors.grey[300]}>
                  ({pct(g.count)})
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                color={colors.grey[300]}
                sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}
              >
                {g.desc}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography
        variant="caption"
        color={colors.grey[300]}
        sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
      >
        Hinweis: {formatNumber(overlap)} Nutzer sind sowohl abgemeldet als auch inaktiv und zählen
        deshalb in beiden Gruppen. Die Prozente oben summieren daher über 100 %.
      </Typography>
    </Box>
  );
};

const renderChurnExplainer = (
  stats: LifetimeStatistics,
  colors: any,
  formatNumber: (n: number) => string,
) => {
  const unsubMedian = stats.unsubscribe.median ?? 0;
  const unsubAvg = stats.unsubscribe.avg ?? 0;
  const engagementMedian = stats.engagement_window.median ?? 0;
  const neverOpenedPct = ((stats.never_opened_count / stats.cohort_size) * 100).toFixed(1);

  return (
    <Box
      sx={{
        mt: 2,
        p: 2.5,
        backgroundColor: colors.primary[400],
        borderLeft: `4px solid ${colors.blueAccent[500]}`,
        borderRadius: 1,
      }}
    >
      <Typography variant="h5" fontWeight="600" color={colors.grey[100]} mb={1}>
        Was bedeuten diese Zahlen?
      </Typography>

      <Typography variant="body2" color={colors.grey[100]} mb={1.5}>
        <strong>Durchschnitt (Ø) vs. Median:</strong> Der Durchschnitt addiert alle Werte und teilt
        durch die Nutzer-Anzahl — einzelne Ausreißer ziehen ihn hoch. Der Median ist der Wert, bei
        dem genau die Hälfte aller Nutzer drüber und die Hälfte drunter liegt. Beispiel: 5 Nutzer
        churnen nach 1, 2, 3, 4 und 100 Tagen → Ø = 22 Tage, Median = 3 Tage. Bei E-Mail-Churn ist
        der Median fast immer die ehrlichere Zahl, weil es (wenige) Nutzer gibt, die extrem lange in
        der Liste bleiben.
      </Typography>

      <Typography variant="body2" color={colors.grey[100]} mb={1.5}>
        <strong>
          Warum &bdquo;Engagement-Fenster&ldquo; statt &bdquo;Tage bis Inaktiv&ldquo;?
        </strong>{' '}
        Die Tage-bis-Inaktiv-Zahl misst unsere Regel ({stats.inactive_rule_days}/
        {stats.inactive_rule_days}), nicht das Verhalten der Nutzer — der Min-Wert wäre per
        Definition immer {stats.inactive_rule_days}. Stattdessen zeigen wir{' '}
        <em>Engagement-Fenster</em> = Tage vom Signup bis zum letzten Open. Das ist der tatsächliche
        Engagement-Zeitraum, bevor der Nutzer verstummt ist. Ein Median von{' '}
        {formatNumber(engagementMedian)} Tagen heißt: die Hälfte der stillen Abwanderer war maximal{' '}
        {formatNumber(engagementMedian)} Tage aktiv, danach Funkstille.
      </Typography>

      <Typography variant="body2" color={colors.grey[100]} mb={1.5}>
        <strong>Was die Zahlen hier konkret sagen:</strong>
      </Typography>
      <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3, color: colors.grey[100] }}>
        <li>
          <Typography variant="body2" component="span">
            <strong>Aktive Abmelder</strong> churnen nach Median {formatNumber(unsubMedian)} Tagen
            (Ø {formatNumber(unsubAvg)}) — meist direkt nach Signup, wenn die Erwartung nicht passt.
            Hier entscheidet das Onboarding.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" component="span">
            <strong>Stille Abwanderer mit Engagement</strong> (
            {formatNumber(stats.engagement_window.count)} Nutzer): Median{' '}
            {formatNumber(engagementMedian)} Tage aktives Engagement, dann Stille. Haben mal
            interessiert — der Inhalt muss sie bei der Stange halten.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" component="span">
            <strong>
              Nie geöffnet: {formatNumber(stats.never_opened_count)} Nutzer ({neverOpenedPct} % der
              Kohorte)
            </strong>{' '}
            — haben sich angemeldet und nie eine Mail geöffnet. Entweder Welcome-Serie zu
            spät/schlecht betreffend, Lead-Qualität schlecht, oder Empfang von Anfang an
            gescheitert.
          </Typography>
        </li>
      </Box>

      <Typography variant="body2" color={colors.grey[100]}>
        <strong>Wichtig zur Einordnung:</strong> Der {stats.inactive_rule_days}
        -Tage-Floor bedeutet, dass Nutzer {'<'} {stats.inactive_rule_days} Tage seit Signup
        regeltechnisch noch nicht als inaktiv gelten können — die Churn-Quote wird in den nächsten
        Wochen natürlich weiter steigen. Inaktive sind außerdem nicht gleich &bdquo;weg für
        immer&ldquo;: sie kriegen weiter die Samstags-SAM-Mail und können reaktivieren.
      </Typography>
    </Box>
  );
};

const UserStatisticsTab: React.FC<UserStatisticsTabProps> = ({
  startDate,
  endDate,
  aggregation,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [growthData, setGrowthData] = useState<GrowthData[]>([]);
  const [lifetimeStats, setLifetimeStats] = useState<LifetimeStatistics | null>(null);
  const [distribution, setDistribution] = useState<DistributionData[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [migrationData, setMigrationData] = useState<MigrationData[]>([]);
  const [migrationReasons, setMigrationReasons] = useState<MigrationReasonsData[]>([]);
  const [listStateData, setListStateData] = useState<ListStateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
    new Set([
      'Anmeldungen (gesamt)',
      'davon heute noch aktiv',
      'davon heute inaktiv',
      'davon bereits abgemeldet',
      'Migriert zu Mailchimp',
    ]),
  );
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, Set<string>>>({});
  // Cohort-trend modal state: which KPI's time-series is open.
  const [trendModal, setTrendModal] = useState<{
    kind: 'unsub' | 'engagement' | 'churn' | 'never_opened';
    horizonDays: number;
  } | null>(null);

  const isHidden = (chartId: string, seriesId: string) =>
    hiddenSeries[chartId]?.has(seriesId) ?? false;

  const toggleHidden = (chartId: string, seriesId: string) => {
    setHiddenSeries((prev) => {
      const next = { ...prev };
      const set = new Set(next[chartId] ?? []);
      if (set.has(seriesId)) {
        set.delete(seriesId);
      } else {
        set.add(seriesId);
      }
      next[chartId] = set;
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const [growth, lifetime, summaryData, migrations, reasons, listState] = await Promise.all([
          fetchData<GrowthData>(
            '/api/iterable-user-statistics/growth',
            {
              start_date: startDate,
              end_date: endDate,
              aggregation,
            },
            true,
          ),
          fetchData<{
            statistics: LifetimeStatistics;
            distribution: DistributionData[];
          }>(
            '/api/iterable-user-statistics/lifetime',
            { start_date: startDate, end_date: endDate },
            false,
          ),
          fetchData<SummaryData>('/api/iterable-user-statistics/summary', {}, false),
          fetchData<MigrationData>(
            '/api/iterable-user-statistics/migrations',
            {
              start_date: startDate,
              end_date: endDate,
              aggregation,
            },
            true,
          ),
          fetchData<MigrationReasonsData>(
            '/api/iterable-user-statistics/migration-reasons',
            {
              start_date: startDate,
              end_date: endDate,
            },
            true,
          ),
          fetchData<ListStateData>(
            '/api/iterable-user-statistics/list-state',
            {
              start_date: startDate,
              end_date: endDate,
              aggregation,
            },
            true,
          ),
        ]);

        if (!cancelled) {
          setGrowthData(growth as GrowthData[]);
          const lifetimeData = lifetime as {
            statistics: LifetimeStatistics;
            distribution: DistributionData[];
          };
          setLifetimeStats(lifetimeData.statistics);
          setDistribution(lifetimeData.distribution);
          setSummary(summaryData as SummaryData);
          setMigrationData(migrations as MigrationData[]);
          setMigrationReasons(reasons as MigrationReasonsData[]);
          setListStateData(listState as ListStateData[]);
        }
      } catch (error) {
        console.error('Error loading Iterable statistics:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, aggregation]);

  // Edge-case detection for the Churn-Analyse block. When the user scopes the
  // cohort to a range where the metrics would mislead, we hide the KPI block
  // and render an info/warning banner explaining why. Order matters: the
  // first matching rule wins.
  const churnEdgeCase = useMemo<{
    severity: 'warning' | 'info';
    title: string;
    message: string;
  } | null>(() => {
    if (!lifetimeStats) return null;
    const ruleDays = lifetimeStats.inactive_rule_days;
    const start = moment(lifetimeStats.cohort_start_date);
    const earliestSafe = moment('2025-07-16');
    const latestSafeForInactive = moment().subtract(ruleDays, 'days');

    if (start.isBefore(earliestSafe)) {
      return {
        severity: 'warning',
        title: 'Kohorte enthält Daten-Artefakte',
        message: `Das gewählte Startdatum (${start.format('DD.MM.YYYY')}) liegt vor dem 16.07.2025. In diesem Zeitraum fand die Mailchimp-Bulk-Migration statt (80.625 Nutzer am 15.07.2025 auf einen Schlag als inaktiv markiert) sowie der Bulk-Import aus Mailchimp (109k Nutzer am 15.05.2025). Churn-Zahlen über diesen Zeitraum vermischen echtes Nutzer-Verhalten mit diesen Artefakten. Bitte Startdatum ≥ 16.07.2025 wählen (empfohlen: ab 01.09.2025 für stabil verwertbare Daten).`,
      };
    }
    if (start.isAfter(latestSafeForInactive)) {
      return {
        severity: 'info',
        title: 'Kohorte zu jung für Inaktivitäts-Analyse',
        message: `Das Startdatum (${start.format('DD.MM.YYYY')}) liegt weniger als ${ruleDays} Tage in der Vergangenheit. Die ${ruleDays}/${ruleDays}-Inaktiv-Regel kann für diese Kohorte noch nicht feuern — alle Zahlen wären strukturell zu niedrig. Bitte Startdatum mindestens ${ruleDays} Tage in die Vergangenheit legen (spätestens ${latestSafeForInactive.format('DD.MM.YYYY')}).`,
      };
    }
    if (lifetimeStats.cohort_size < 500) {
      return {
        severity: 'info',
        title: 'Kohorte zu klein für robuste Statistik',
        message: `Diese Kohorte hat nur ${formatNumber(lifetimeStats.cohort_size)} Nutzer. Einzelne Ausreißer kippen die Zahlen, Median-Werte werden instabil. Erweitere den Zeitraum für aussagekräftige Analyse (Ziel: 5.000+ Nutzer).`,
      };
    }
    if (lifetimeStats.exit_count === 0) {
      return {
        severity: 'info',
        title: 'Keine Exit-Ereignisse in dieser Kohorte',
        message:
          'Im gewählten Zeitraum hat noch kein einziger Nutzer dieser Kohorte die Liste verlassen — Churn-Analyse nicht sinnvoll.',
      };
    }
    return null;
  }, [lifetimeStats]);

  const handleSeriesToggle = (seriesId: string) => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) {
        next.delete(seriesId);
      } else {
        next.add(seriesId);
      }
      return next;
    });
  };

  // Prepare chart data
  const growthAllSeries = useMemo(() => {
    if (!growthData.length) return [];

    const migrationMap = new Map<string, MigrationData>();
    migrationData.forEach((m) => migrationMap.set(m.date, m));

    const series = [
      {
        id: 'Anmeldungen (gesamt)',
        color: colors.grey[300],
        data: growthData.map((d) => ({ x: d.date, y: d.new_signups })),
      },
      {
        id: 'davon heute noch aktiv',
        color: colors.greenAccent[500],
        data: growthData.map((d) => ({
          x: d.date,
          y: d.new_active_subscribed,
        })),
      },
      {
        id: 'davon heute inaktiv',
        color: colors.grey[400],
        data: growthData.map((d) => ({
          x: d.date,
          y: d.new_inactive_subscribed,
        })),
      },
      {
        id: 'davon bereits abgemeldet',
        color: colors.redAccent[500],
        data: growthData.map((d) => ({ x: d.date, y: d.new_unsubscribed })),
      },
    ];

    if (migrationData.length > 0) {
      series.push(
        {
          id: 'Migriert zu Mailchimp',
          color: colors.blueAccent[300],
          data: growthData.map((d) => {
            const migration = migrationMap.get(d.date);
            return {
              x: d.date,
              y: migration?.migrations_to_mailchimp || 0,
            };
          }),
        },
        {
          id: 'Remigriert zu Iterable',
          color: '#9c27b0',
          data: growthData.map((d) => {
            const migration = migrationMap.get(d.date);
            return {
              x: d.date,
              y: migration?.migrations_to_iterable || 0,
            };
          }),
        },
      );
    }

    return series;
  }, [growthData, migrationData, colors]);

  const growthChartData = useMemo(
    () => growthAllSeries.filter((s) => visibleSeries.has(s.id)),
    [growthAllSeries, visibleSeries],
  );

  const cumulativeChartData = useMemo(() => {
    if (!growthData.length) return [];

    return [
      {
        id: 'Kumulative Nutzer',
        color: colors.greenAccent[500],
        data: growthData.map((d) => ({ x: d.date, y: d.cumulative_users })),
      },
    ];
  }, [growthData, colors]);

  const distributionChartData = useMemo(() => {
    return distribution.map((d) => ({
      bucket: d.lifetime_bucket,
      count: d.count,
    }));
  }, [distribution]);

  const migrationReasonsChartData = useMemo(() => {
    if (!migrationReasons.length) return [];

    return migrationReasons.map((mr) => ({
      id: `${mr.migration_type === 'iterable_to_mailchimp' ? 'Zu Mailchimp' : 'Zu Iterable'}: ${mr.reason}`,
      label: `${mr.migration_type === 'iterable_to_mailchimp' ? 'Zu Mailchimp' : 'Zu Iterable'}: ${mr.reason}`,
      value: mr.count,
    }));
  }, [migrationReasons]);

  const totalListDevelopmentAllSeries = useMemo(() => {
    if (!listStateData.length) return [];

    return [
      {
        id: 'Gesamt-Nutzer',
        color: colors.greenAccent[500],
        data: listStateData.map((d) => ({ x: d.date, y: d.total_users })),
      },
      {
        id: 'Aktive Subscriber',
        color: colors.blueAccent[500],
        data: listStateData.map((d) => ({
          x: d.date,
          y: d.active_subscribed,
        })),
      },
      {
        id: 'Inaktive Subscriber',
        color: colors.grey[400],
        data: listStateData.map((d) => ({
          x: d.date,
          y: d.inactive_subscribed,
        })),
      },
      {
        id: 'Abgemeldete Nutzer',
        color: colors.redAccent[500],
        data: listStateData.map((d) => ({
          x: d.date,
          y: d.unsubscribed_users,
        })),
      },
    ];
  }, [listStateData, colors]);

  const totalListDevelopmentChartData = useMemo(() => {
    const hidden = hiddenSeries['totalList'];
    return totalListDevelopmentAllSeries.filter((s) => !(hidden?.has(s.id) ?? false));
  }, [totalListDevelopmentAllSeries, hiddenSeries]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE').format(Math.round(num));
  };

  const formatPercentage = (num: number, total: number) => {
    return ((num / total) * 100).toFixed(1) + '%';
  };

  const churnHeaderSx = {
    backgroundColor: '#e0e0e0 !important',
    color: 'black',
    fontWeight: 'bold',
  };

  const renderChurnHeaderCell = (label: string, tooltip: string) => (
    <TableCell sx={churnHeaderSx} align="right">
      <Tooltip title={tooltip} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'help',
          }}
        >
          {label}
          <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.6 }} />
        </Box>
      </Tooltip>
    </TableCell>
  );

  const renderChurnRow = (
    label: string,
    metric: ChurnMetric,
    fmt: (n: number) => string,
    highlight = false,
    rowTooltip?: string,
    cohortSize?: number,
  ) => {
    const cellSx = highlight ? { fontWeight: 'bold' } : undefined;
    const labelNode = rowTooltip ? (
      <Tooltip title={rowTooltip} arrow placement="right">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'help',
          }}
        >
          {label}
          <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.5 }} />
        </Box>
      </Tooltip>
    ) : (
      label
    );
    const pctOfCohort =
      cohortSize && cohortSize > 0 ? ((metric.count / cohortSize) * 100).toFixed(1) + ' %' : '–';
    return (
      <TableRow sx={highlight ? { backgroundColor: '#f5f5f5' } : undefined}>
        <TableCell sx={cellSx}>{labelNode}</TableCell>
        <TableCell align="right" sx={cellSx}>
          {metric.avg === null ? '–' : fmt(metric.avg)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {metric.median === null ? '–' : fmt(metric.median)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {metric.min === null ? '–' : fmt(metric.min)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {metric.max === null ? '–' : fmt(metric.max)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {fmt(metric.count)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {pctOfCohort}
        </TableCell>
      </TableRow>
    );
  };

  const LineTooltip = ({ point }: any) => (
    <Box
      sx={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {point.serieId}
      </Typography>
      <Typography variant="body2">
        <strong>Datum:</strong> {point.data.xFormatted}
      </Typography>
      <Typography variant="body2">
        <strong>Anzahl:</strong> {formatNumber(point.data.yFormatted)}
      </Typography>
    </Box>
  );

  const BarTooltip = ({ id, value, indexValue }: any) => (
    <Box
      sx={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {indexValue}
      </Typography>
      <Typography variant="body2">
        <strong>Anzahl:</strong> {formatNumber(value)}
      </Typography>
    </Box>
  );

  const PieTooltip = ({ datum }: any) => (
    <Box
      sx={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {datum.label}
      </Typography>
      <Typography variant="body2">
        <strong>Anzahl:</strong> {formatNumber(datum.value)}
      </Typography>
    </Box>
  );

  const nivoTheme = useMemo(
    () => ({
      axis: {
        domain: { line: { stroke: colors.grey[100] } },
        legend: { text: { fill: colors.grey[100] } },
        ticks: {
          line: { stroke: colors.grey[100], strokeWidth: 1 },
          text: { fill: colors.grey[100] },
        },
      },
      legends: { text: { fill: colors.grey[100] } },
      tooltip: { container: { color: colors.primary[500] } },
    }),
    [colors],
  );

  const buildClickableLegend = (
    allSeries: { id: string; color: string }[],
    hiddenCheck: (seriesId: string) => boolean,
    onToggle: (seriesId: string) => void,
    overrides: Record<string, any> = {},
  ) => ({
    anchor: 'bottom-right' as const,
    direction: 'column' as const,
    justify: false,
    translateX: 150,
    translateY: 0,
    itemsSpacing: 2,
    itemDirection: 'left-to-right' as const,
    itemWidth: 140,
    itemHeight: 20,
    itemOpacity: 0.75,
    symbolSize: 12,
    symbolShape: 'circle' as const,
    symbolBorderColor: 'rgba(0, 0, 0, .5)',
    effects: [
      {
        on: 'hover' as const,
        style: { itemBackground: 'rgba(0, 0, 0, .03)', itemOpacity: 1 },
      },
    ],
    ...overrides,
    data: allSeries.map((s) => ({
      id: s.id,
      label: s.id,
      color: hiddenCheck(s.id) ? colors.grey[500] : s.color,
    })),
    onClick: (datum: any) => onToggle(String(datum.id)),
  });

  return (
    <Box>
      {/* Loading indicator — slim bar at top during any fetch. Keeps existing
          content visible during re-fetches (e.g., when user changes dates). */}
      {loading && (
        <LinearProgress
          sx={{
            mb: 2,
            height: 3,
            borderRadius: 1,
          }}
        />
      )}

      {/* Info Alert */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: colors.primary[400],
          borderLeft: `4px solid ${colors.blueAccent[500]}`,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color={colors.grey[100]}>
          <strong>Hinweise zum Dual ESP System:</strong>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>
              <strong>Aktive Subscriber (Iterable):</strong> Nutzer die E-Mails über Iterable
              erhalten (aktives Engagement)
            </li>
            <li>
              <strong>Inaktive Subscriber:</strong> Nutzer, die seit 90+ Tagen keine Mail mehr
              geöffnet haben und seit mind. 90 Tagen angemeldet sind (90/90-Regel). Erhalten nur
              noch die Samstags-SAM-Mail zur Reaktivierung.
            </li>
            <li>
              <strong>Inactive-Status:</strong> Wird frühestens 90 Tage nach Signup gesetzt, wenn in
              dieser Zeit keine einzige Mail geöffnet wurde. (Historisch: bis Sep 2025 galt eine
              30/30-Regel.)
            </li>
            <li>
              <strong>ESP-Migrationen (abgeschlossen):</strong> Am 15.07.2025 wurden 80.625 inaktive
              Nutzer zu Mailchimp migriert (Domain-Reputations-Schutz). Zwischen 14.10.2025 und Feb
              2026 lief die graduelle Rückmigration zu Iterable (~15k/Monat). Seit März 2026
              effektiv abgeschlossen — wir sind zurück auf Single-ESP (Iterable). Die
              dahinterliegende esp_user_migrations-Pipeline feuert seit Oktober 2025 keine neuen
              Migrationen mehr.
            </li>
            <li>Iterable-Integration gestartet im April 2025</li>
            <li>Bulk-Import von 108k Nutzern im Mai 2025 (Mailchimp-Migration)</li>
            <li>Bulk ESP-Migration zu Mailchimp am 15. Juli 2025 (80.625 Nutzer)</li>
            <li>Abmelde-Events nur ab Juli 2025 verfügbar</li>
            <li>
              <strong>Churn-Analyse:</strong> Kohorte sind Nutzer mit Signup ab{' '}
              {lifetimeStats ? moment(lifetimeStats.cohort_start_date).format('DD.MM.YYYY') : '...'}{' '}
              ({lifetimeStats ? formatNumber(lifetimeStats.cohort_size) : '...'} Nutzer). Vor diesem
              Datum war das Tracking durch die Massen- Migration zu Mailchimp (15.07.2025) und
              Dual-ESP-Instabilitäten verzerrt.
            </li>
            <li>
              <strong>Inaktiv-Datum analytisch abgeleitet:</strong> Die esp_user_migrations-Pipeline
              markiert seit Oktober 2025 keine neuen Inaktiven mehr. Wir reproduzieren die
              90/90-Regel (&gt;90 Tage ohne Open bei &gt;90 Tagen seit Signup) direkt aus dem
              Event-Log pro Nutzer.
            </li>
          </ul>
        </Typography>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                    Gesamt-Nutzer
                  </Typography>
                  <Tooltip
                    title="Enthält ~109k Bulk-Import aus der Mailchimp-Migration am 15.05.2025 — das sind keine organisch gewachsenen Nutzer, sondern der Altbestand. Für organische Neuanmeldungen siehe den 'Kumulative Nutzer-Entwicklung'-Chart unten (Werte nach Juli 2025)."
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: colors.grey[300],
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography color={colors.greenAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(summary.total_users)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Aktive Subscriber
                </Typography>
                <Typography color={colors.greenAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(summary.active_subscribed)}
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  {formatPercentage(summary.active_subscribed, summary.subscribed_users)} der
                  Subscriber
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                    Inaktive Subscriber
                  </Typography>
                  <Tooltip
                    title="Subscriber, die 90+ Tage keine Mail geöffnet haben (90/90-Regel). Erhalten nur noch die Samstags-SAM-Mail zur Reaktivierung. Sind technisch noch angemeldet — im Gegensatz zu den Abgemeldeten daneben."
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: colors.grey[300],
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography color={colors.grey[400]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(summary.inactive_subscribed)}
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  {formatPercentage(summary.inactive_subscribed, summary.subscribed_users)} der
                  Subscriber
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Abgemeldete Nutzer
                </Typography>
                <Typography color={colors.redAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(summary.unsubscribed_users)}
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  {formatPercentage(summary.unsubscribed_users, summary.total_users)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Churn Analysis — guardrail for edge-case ranges */}
      {lifetimeStats && churnEdgeCase && (
        <Alert severity={churnEdgeCase.severity} sx={{ mb: 3 }}>
          <AlertTitle>{churnEdgeCase.title}</AlertTitle>
          {churnEdgeCase.message}
        </Alert>
      )}

      {/* Churn Analysis Cards + Breakdown — rendered only when the selected
          range produces trustworthy numbers */}
      {lifetimeStats && !churnEdgeCase && (
        <Box mb={3}>
          <Typography variant="h4" color={colors.grey[100]} fontWeight="600" mb={0.5}>
            Churn-Analyse
          </Typography>
          <Typography color={colors.grey[300]} variant="body2" mb={2}>
            Kohorte: Nutzer mit Signup ab{' '}
            {moment(lifetimeStats.cohort_start_date).format('DD.MM.YYYY')} (
            {formatNumber(lifetimeStats.cohort_size)} Nutzer) — Inaktiv-Status analytisch aus
            Event-Log abgeleitet ({lifetimeStats.inactive_rule_days}/
            {lifetimeStats.inactive_rule_days}-Regel: &gt;
            {lifetimeStats.inactive_rule_days} Tage ohne Open bei &gt;
            {lifetimeStats.inactive_rule_days} Tagen seit Anmeldung).
          </Typography>

          {/* User group breakdown — legend */}
          {renderCohortGroupLegend(lifetimeStats, colors, formatNumber)}

          {/* Headline cards */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              {renderKpiCard({
                title: 'Median Tage bis Abmeldung',
                tooltip: `Die Hälfte der aktiven Abmelder klickt innerhalb dieser Zeit auf 'Abmelden', die andere Hälfte später. Zeigt den "typischen" Abmelder.

Warum Median statt Durchschnitt? Die Verteilung der Abmelde-Zeit ist stark rechts-schief: die meisten melden sich sehr schnell ab (oft in den ersten Tagen), aber einzelne Spät-Abmelder nach 100+ Tagen ziehen den Durchschnitt massiv nach oben. Konkret bei dieser Kohorte: Ø = ${formatNumber(lifetimeStats.unsubscribe.avg ?? 0)} Tage, Median = ${formatNumber(lifetimeStats.unsubscribe.median ?? 0)} Tage — der Ø überschätzt den typischen Fall um mehr als 2×. Für Marketing-Entscheidungen zum Onboarding-Fenster ist der Median die ehrliche Zahl.

Beispiel: 3 Nutzer melden sich nach 2, 5 und 100 Tagen ab → Durchschnitt = 36 Tage (verzerrt vom Ausreißer), Median = 5 Tage (der typische Fall).

Enthält nur ${formatNumber(lifetimeStats.unsubscribe.count)} aktive Abmelder, keine stillen Abwanderer. Der Durchschnitt bleibt in der Tabelle unten für die ausreißer-sensitive Perspektive sichtbar.`,
                value: `${formatNumber(lifetimeStats.unsubscribe.median ?? 0)} Tage`,
                valueColor: colors.greenAccent[500],
                subtitle: `${formatNumber(lifetimeStats.unsubscribe.count)} aktive Abmelder (${((lifetimeStats.unsubscribe.count / lifetimeStats.cohort_size) * 100).toFixed(1)} % der Kohorte) — Ø liegt bei ${formatNumber(lifetimeStats.unsubscribe.avg ?? 0)} Tagen`,
                colors,
                onTrendClick: () => setTrendModal({ kind: 'unsub', horizonDays: 30 }),
              })}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderKpiCard({
                title: 'Median Engagement-Fenster',
                tooltip: `Betrifft NUR die "stillen Abwanderer" (${formatNumber(lifetimeStats.engagement_window.count)} Nutzer): das sind Nutzer, die seit ${lifetimeStats.inactive_rule_days}+ Tagen keine Mail mehr geöffnet haben, aber vorher mindestens 1x geöffnet hatten.

Für jeden dieser Nutzer messen wir: wie viele Tage hat er/sie nach Anmeldung aktiv mitgemacht, bevor Funkstille kam?

Beispiel Anna: Signup 01.09., letztes Open am 14.09. → 13 Tage Engagement-Fenster.

Median = die Hälfte dieser stillen Abwanderer war maximal so viele Tage aktiv. Kleiner Wert = wir verlieren Nutzer schnell nach Signup.

Noch-aktive Nutzer sind NICHT hier drin (deren Fenster ist noch offen). Nie-Öffner auch nicht (siehe rote Box unten).`,
                value: `${formatNumber(lifetimeStats.engagement_window.median ?? 0)} Tage`,
                valueColor: colors.blueAccent[500],
                subtitle: `${formatNumber(lifetimeStats.engagement_window.count)} stille Abwanderer mit mind. 1 Open (${((lifetimeStats.engagement_window.count / lifetimeStats.cohort_size) * 100).toFixed(1)} % der Kohorte)`,
                colors,
                onTrendClick: () => setTrendModal({ kind: 'engagement', horizonDays: 90 }),
              })}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderKpiCard({
                title: 'Churn-Quote',
                tooltip: `Wie viel Prozent der Kohorte ist bereits "weg"?

"Weg" = entweder aktiv abgemeldet ODER seit ${lifetimeStats.inactive_rule_days}+ Tagen keine Mail mehr geöffnet (unsere interne Regel).

Wichtig: "inaktiv" heißt nicht "für immer weg". Diese Nutzer kriegen weiter die Samstags-SAM-Mail und können theoretisch reaktivieren. Nur die Abgemeldeten sind endgültig weg.

Der Rest (${formatNumber(lifetimeStats.cohort_size - lifetimeStats.exit_count)} Nutzer) ist noch aktiv — deren Exit steht noch aus oder kommt vielleicht nie. Die Zahl wird in den kommenden Wochen natürlich weiter steigen, weil neue Nutzer noch Zeit brauchen, um die 90-Tage-Grenze zu erreichen.`,
                value: `${(lifetimeStats.exit_rate * 100).toFixed(1)} %`,
                valueColor: colors.redAccent[500],
                subtitle: `${((lifetimeStats.unsubscribe.count / lifetimeStats.cohort_size) * 100).toFixed(0)} % abgemeldet, ${((lifetimeStats.inactive.count / lifetimeStats.cohort_size) * 100).toFixed(0)} % inaktiv (teils überlappend)`,
                colors,
                onTrendClick: () => setTrendModal({ kind: 'churn', horizonDays: 30 }),
              })}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderKpiCard({
                title: 'Nie geöffnet',
                tooltip: `Nutzer, die sich angemeldet haben und dann keine einzige Mail geöffnet haben, bevor die ${lifetimeStats.inactive_rule_days}-Tage-Regel sie als inaktiv markiert hat. Die reinste Lead-Qualitäts- bzw. Welcome-Serie-Kennzahl auf dieser Seite.

Warum prominent? 43 % aller stillen Abwanderer gehören in diese Gruppe — fast die Hälfte des stillen Churns kommt nicht aus "enttäuschtem Nutzer", sondern aus "Nutzer hat uns nie wahrgenommen". Das ist ein anderer Hebel (Lead-Quelle / Welcome-Serie-Deliverability) als klassische Retention-Arbeit.

Mögliche Ursachen: Welcome-Serie landet im Spam oder zu spät, Lead-Quelle liefert niedrige Qualität (z.B. Gewinnspiel-Anmeldungen), falsche E-Mail beim Signup. Kleiner Mess-Caveat: Outlook-Nutzer mit deaktivierten Bildern könnten technisch klicken ohne einen Open-Event auszulösen — das betrifft <2 % der Gruppe und ändert die Größenordnung nicht.`,
                value: `${((lifetimeStats.never_opened_count / lifetimeStats.cohort_size) * 100).toFixed(1)} %`,
                valueColor: colors.grey[400],
                subtitle: `${formatNumber(lifetimeStats.never_opened_count)} Nutzer — ${((lifetimeStats.never_opened_count / Math.max(lifetimeStats.inactive.count, 1)) * 100).toFixed(0)} % aller stillen Abwanderer`,
                colors,
                onTrendClick: () => setTrendModal({ kind: 'never_opened', horizonDays: 30 }),
              })}
            </Grid>
          </Grid>

          {/* Breakdown table */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={churnHeaderSx}>Kennzahl</TableCell>
                  {renderChurnHeaderCell(
                    'Ø Tage',
                    `Durchschnitt: alle Tage-Werte addiert, durch Anzahl Nutzer geteilt.

Vorsicht: ein einzelner Spätfall (z.B. jemand, der sich erst nach 200 Tagen abmeldet) zieht den Schnitt deutlich nach oben. Deshalb liegt der Durchschnitt oft weit über dem "typischen" Wert.

Beispiel: Nutzer A churnt nach 2 Tagen, Nutzer B nach 5 Tagen, Nutzer C nach 100 Tagen → Durchschnitt = 36 Tage, obwohl 2 von 3 Nutzern unter 10 Tagen churnen.`,
                  )}
                  {renderChurnHeaderCell(
                    'Median',
                    `Der mittlere Wert. Sortiert man alle Nutzer nach Tagen, ist der Median derjenige genau in der Mitte: 50 % der Nutzer haben einen kleineren Wert, 50 % einen größeren.

Vorteil: Ausreißer stören nicht. Der Median zeigt den "typischen" Nutzer und ist fast immer ehrlicher als der Durchschnitt.

Bei dem Beispiel oben (2, 5, 100 Tage): Median = 5 Tage. Das beschreibt die Realität besser.`,
                  )}
                  {renderChurnHeaderCell(
                    'Min',
                    `Der schnellste Fall in dieser Zeile. Bei Abmeldungen: jemand, der sich unmittelbar nach Signup abgemeldet hat (0 Tage). Bei Engagement-Fenster: jemand, der am Signup-Tag geöffnet hat und danach nie wieder (0 Tage).`,
                  )}
                  {renderChurnHeaderCell(
                    'Max',
                    `Der bisher längste Fall. Diese Zahl ist durch das Alter der Kohorte begrenzt: Wer sich am 01.09.2025 angemeldet hat, kann aktuell höchstens ~230 Tage in der Liste sein. Mit der Zeit wird der Max-Wert also natürlich weiter wachsen.`,
                  )}
                  {renderChurnHeaderCell(
                    'Nutzer',
                    `Wie viele Nutzer aus der Kohorte von ${formatNumber(lifetimeStats.cohort_size)} in dieser Kategorie gelandet sind. Nicht alle Nutzer sind in allen Zeilen — die 'Bis Abmeldung'-Zeile z.B. zählt nur Nutzer, die wirklich auf 'Abmelden' geklickt haben.`,
                  )}
                  {renderChurnHeaderCell(
                    '% Kohorte',
                    `Anteil dieser Gruppe an der gesamten Kohorte von ${formatNumber(lifetimeStats.cohort_size)} Nutzern. Die Zeilen können sich überschneiden (z.B. Nutzer, die sich abgemeldet haben UND auch die 90-Tage-Inaktivitätsschwelle erreicht haben) — die Prozente summieren daher nicht zu 100 %.`,
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {renderChurnRow(
                  'Bis Abmeldung',
                  lifetimeStats.unsubscribe,
                  formatNumber,
                  false,
                  `Gezählte Tage: Anmeldung → Klick auf 'Abmelden'-Link.

Enthält nur Nutzer, die sich aktiv abgemeldet haben (${formatNumber(lifetimeStats.unsubscribe.count)} in dieser Kohorte). Stille Abwanderer und noch-aktive Nutzer sind NICHT hier drin.

Die sauberste Churn-Zahl — eindeutig messbar, ohne Interpretationsspielraum.`,
                  lifetimeStats.cohort_size,
                )}
                {renderChurnRow(
                  'Engagement-Fenster (stille Abwanderer)',
                  lifetimeStats.engagement_window,
                  formatNumber,
                  true,
                  `Gezählte Tage: Anmeldung → letztes Open (bevor ${lifetimeStats.inactive_rule_days}+ Tage Funkstille kamen).

Beispiel: Nutzer meldet sich am 01.09. an, öffnet Mails am 02.09., 05.09. und 14.09. Danach nie wieder — und inzwischen sind ${lifetimeStats.inactive_rule_days}+ Tage vergangen. Engagement-Fenster = 13 Tage (Signup → 14.09.).

Enthält nur "stille Abwanderer mit Opens" (${formatNumber(lifetimeStats.engagement_window.count)} Nutzer). Nie-Öffner stehen in der roten Box unter der Tabelle. Abgemeldete und noch-aktive Nutzer zählen hier nicht mit.`,
                  lifetimeStats.cohort_size,
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Interpretation / explainer */}
          {renderChurnExplainer(lifetimeStats, colors, formatNumber)}
        </Box>
      )}

      {/* Total List Development Chart */}
      <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gesamte List-Entwicklung
        </Typography>
        <Typography
          variant="caption"
          sx={{
            padding: '0 30px',
            display: 'block',
            color: colors.grey[300],
            fontStyle: 'italic',
          }}
        >
          Echte historische Tages-Snapshots verfügbar ab 14.10.2025 — davor wurden keine täglichen
          Zustände gespeichert, der Chart beginnt daher an diesem Datum.
        </Typography>
        <Box height="350px" mt="-20px">
          <ResponsiveLine
            data={totalListDevelopmentChartData}
            margin={{ top: 50, right: 160, bottom: 50, left: 60 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            curve="monotoneX"
            tooltip={LineTooltip}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Datum',
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Anzahl Nutzer',
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            colors={{ datum: 'color' }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={true}
            legends={[
              buildClickableLegend(
                totalListDevelopmentAllSeries,
                (id) => isHidden('totalList', id),
                (id) => toggleHidden('totalList', id),
              ),
            ]}
            theme={nivoTheme}
          />
        </Box>
      </Box>

      {/* Growth Chart */}
      <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Neue Nutzer-Anmeldungen
        </Typography>
        <Typography
          variant="caption"
          sx={{
            padding: '0 30px',
            display: 'block',
            color: colors.grey[300],
            fontStyle: 'italic',
          }}
        >
          Die Breakdown-Linien (&bdquo;davon heute …&ldquo;) zeigen den <strong>heutigen</strong>{' '}
          Status der damaligen Signups — nicht den Status am Signup-Tag. Ein Nutzer, der sich im
          Sep. 2025 angemeldet und inzwischen abgemeldet hat, erscheint auf dem Sep-Balken als
          &bdquo;bereits abgemeldet&ldquo;.
        </Typography>

        {/* Series visibility toggles */}
        <Box sx={{ padding: '0 30px 10px 30px' }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('Anmeldungen (gesamt)')}
                  onChange={() => handleSeriesToggle('Anmeldungen (gesamt)')}
                  sx={{ color: colors.grey[300] }}
                />
              }
              label="Anmeldungen (gesamt)"
              sx={{ color: colors.grey[100] }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('davon heute noch aktiv')}
                  onChange={() => handleSeriesToggle('davon heute noch aktiv')}
                  sx={{ color: colors.greenAccent[500] }}
                />
              }
              label="davon heute noch aktiv"
              sx={{ color: colors.grey[100] }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('davon heute inaktiv')}
                  onChange={() => handleSeriesToggle('davon heute inaktiv')}
                  sx={{ color: colors.grey[400] }}
                />
              }
              label="davon heute inaktiv"
              sx={{ color: colors.grey[100] }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('davon bereits abgemeldet')}
                  onChange={() => handleSeriesToggle('davon bereits abgemeldet')}
                  sx={{ color: colors.redAccent[500] }}
                />
              }
              label="davon bereits abgemeldet"
              sx={{ color: colors.grey[100] }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('Migriert zu Mailchimp')}
                  onChange={() => handleSeriesToggle('Migriert zu Mailchimp')}
                  sx={{ color: colors.blueAccent[300] }}
                />
              }
              label="Migriert zu Mailchimp"
              sx={{ color: colors.grey[100] }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleSeries.has('Remigriert zu Iterable')}
                  onChange={() => handleSeriesToggle('Remigriert zu Iterable')}
                  sx={{ color: '#9c27b0' }}
                />
              }
              label="Remigriert zu Iterable"
              sx={{ color: colors.grey[100] }}
            />
          </FormGroup>
        </Box>

        <Box height="350px" mt="-20px">
          <ResponsiveLine
            data={growthChartData}
            margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
            }}
            curve="monotoneX"
            tooltip={LineTooltip}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Datum',
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Anzahl Nutzer',
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            colors={{ datum: 'color' }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            legends={[
              buildClickableLegend(
                growthAllSeries,
                (id) => !visibleSeries.has(id),
                (id) => handleSeriesToggle(id),
                { translateX: 100, itemWidth: 140 },
              ),
            ]}
            theme={nivoTheme}
          />
        </Box>
      </Box>

      {/* Cumulative Users Chart */}
      <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Kumulative Nutzer-Entwicklung
        </Typography>
        <Typography
          variant="caption"
          sx={{
            padding: '0 30px',
            display: 'block',
            color: colors.grey[300],
            fontStyle: 'italic',
          }}
        >
          Hinweis: Am 15.05.2025 springt die Kurve um +109k durch den Mailchimp-Bulk-Import
          (Alt-Bestand, kein organischer Zuwachs). Organisches Wachstum ab ~Juli 2025 sichtbar.
        </Typography>
        <Box height="350px" mt="-20px">
          <ResponsiveLine
            data={cumulativeChartData}
            margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            curve="monotoneX"
            tooltip={LineTooltip}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Datum',
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Gesamt-Nutzer',
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            colors={{ datum: 'color' }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={true}
            theme={nivoTheme}
          />
        </Box>
      </Box>

      {/* ESP Migration Reasons Pie Chart */}
      {migrationReasons.length > 0 && (
        <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
          <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
            ESP-Migrations-Gründe (historisch)
          </Typography>
          <Typography
            variant="caption"
            sx={{
              padding: '0 30px',
              display: 'block',
              color: colors.grey[300],
              fontStyle: 'italic',
            }}
          >
            Pipeline lief von Jul 2025 bis Sep 2025 (Iterable→Mailchimp) und Okt 2025 bis Feb 2026
            (Rückmigration). Seither keine neuen Migrationen — die Verteilung unten ist damit
            statisch.
          </Typography>
          <Box height="450px" mt="-20px">
            <ResponsivePie
              data={migrationReasonsChartData}
              margin={{ top: 40, right: 280, bottom: 40, left: 80 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              tooltip={PieTooltip}
              borderWidth={1}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 0.2]],
              }}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={colors.grey[100]}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              arcLabelsSkipAngle={10}
              arcLabelsTextColor={{
                from: 'color',
                modifiers: [['darker', 2]],
              }}
              legends={[
                {
                  anchor: 'right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 8,
                  itemWidth: 250,
                  itemHeight: 20,
                  itemTextColor: colors.grey[100],
                  itemDirection: 'left-to-right',
                  itemOpacity: 1,
                  symbolSize: 14,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover' as const,
                      style: {
                        itemTextColor: colors.grey[100],
                      },
                    },
                  ],
                },
              ]}
              theme={{
                tooltip: {
                  container: {
                    color: colors.primary[500],
                  },
                },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Lifetime Distribution Chart */}
      <Box sx={{ backgroundColor: colors.primary[400] }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Verteilung: Tage bis Exit (Kohorte ab 01.09.2025)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            padding: '0 30px',
            display: 'block',
            color: colors.grey[300],
            fontStyle: 'italic',
          }}
        >
          Jeder Bucket zählt Nutzer, die bis heute ein Exit-Ereignis hatten (Abmeldung oder
          90/90-Inaktivität, je nachdem was zuerst kam). Der 90-120-Bucket ist typischerweise gross,
          weil die 90/90-Regel erst nach 90 Tagen feuern kann — Nutzer, die nach dem Signup nie
          öffnen, landen daher praktisch immer genau auf Tag 90.
        </Typography>
        <Box height="350px" mt="-20px">
          <ResponsiveBar
            data={distributionChartData}
            keys={['count']}
            indexBy="bucket"
            margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={{ scheme: 'nivo' }}
            tooltip={BarTooltip}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 1.6]],
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Lebensdauer',
              legendPosition: 'middle',
              legendOffset: 42,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Anzahl Nutzer',
              legendPosition: 'middle',
              legendOffset: -50,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
              from: 'color',
              modifiers: [['darker', 1.6]],
            }}
            theme={nivoTheme}
          />
        </Box>
      </Box>

      {/* Cohort-trend modal for headline KPI cards (mounted once, opened via card onClick) */}
      {trendModal && (
        <ChurnKpiTrendModal
          open={!!trendModal}
          onClose={() => setTrendModal(null)}
          kind={trendModal.kind}
          horizonDays={trendModal.horizonDays}
        />
      )}
    </Box>
  );
};

export default UserStatisticsTab;
